import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useDispatch, useSelector } from 'react-redux';
import { NavigationService } from '../services/navigation.service';

import { ToastService } from 'services/toast.service';
import { UserService } from '../services/user.service';
import { ClientService } from '../services/client.service';

import { ROUTES } from '../constants/navigation.constants';
import { MAX_LENGTH_ENTITY } from 'constants/limite.constants';

import { CommonGlobalService, MultiSelect, Option, RootState, UserFilter, UserLinked } from '@umanforge/common';

import { HomeTopBar } from '../components/menu/home_top_bar.component';
import { Breadcrumb } from '../components/menu/breadcrumb.component';
import { Button } from '../components/inputs/button.component';
import { Input } from '../components/inputs/input.component';
import { Loading } from '../components/loading.component';
import { SwitchAdminUser } from 'components/users/switch_admin_user.component';
import { HomeLeftBar } from 'components/menu/home_left_bar.component';

import { SaveIcon } from '../components/icons/save_icon.component';
import { ClientsIcon } from '../components/icons/clients_icon.component';
import { UsersIcon } from '../components/icons/users_icon.component';
import { PlusIcon } from '../components/icons/plus_icon.component';

const toastService: ToastService = new ToastService( 'Create Client' );


export function CreateClientView() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const me = useSelector( ( state: RootState ) => { return state.user.value; } );
  const dispatch = useDispatch();

  /*----------------------------- useState ------------------------------*/
  const [ clientName, setClientName ] = useState<string>( '' );
  const [ selectedAdmins, setSelectedAdmins ] = useState<Option<UserLinked>[]>( [] );

  const [ canPost, setCanPost ] = useState<boolean>( false );
  const [ hasChange, setHasChange ] = useState<boolean>( false );

  const [ isLoading, setIsLoading ] = useState<boolean>( false );

  /*---------------------------- useEffect ------------------------------*/
  useEffect( () => {
    if ( me && !me.isSuperAdmin ) {
      NavigationService.toClients( navigate, false );
    }
  }, [] );

  useEffect( () => {
    checkCanPostClient();
  }, [ clientName ] );

  useEffect( () => {
    checkHasChangeClient();
  }, [ clientName, selectedAdmins ] );

  /*---------------------------- Functions ------------------------------*/
  const checkCanPostClient = () => {
    const nameChange = clientName != null && clientName != '';
    setCanPost( nameChange );
  };
  const checkHasChangeClient = () => {
    const nameChange = clientName != null && clientName != '';
    const adminChange = selectedAdmins.length > 0;
    setHasChange( nameChange || adminChange );
  };

  const onCreateClick = async () => {
    setIsLoading( true );
    const newClientId = await ClientService.postClient( { name: clientName, users: selectedAdmins.map( user => { return { id: user.object.id, isAdmin: user.object.isAdmin }; } ) }, token, dispatch );
    if ( newClientId != null ) {
      toastService.showSuccess( t( 'The {0} client has been created.' ).replace( '{0}', ( clientName ) ) );
      resetForm();
    }
    setIsLoading( false );
  };

  const getUsers = async ( filter: { page: number, step: number, filterName?: string | undefined } ): Promise<{ total: number, list: Option<UserLinked>[] }> => {
    if ( filter.filterName === undefined || filter.filterName === '' ) {
      delete filter.filterName;
    }
    const result = ( await UserService.getAllowedUsers( filter as UserFilter, token, dispatch ) );
    return { total: result.total, list: CommonGlobalService.toOptions<UserLinked>(
      result.users.map<UserLinked>( user => {
        return {
          id: user.id,
          fullname: user.fullname,
          isDisable: user.isDisable,
          isAdminOfUser: user.isAdminOfUser,
          isAdmin: true,
        };
      } ) )
    };
  };

  const resetForm = () => {
    setClientName( '' );
    setSelectedAdmins( [] );
  };

  /*------------------------------- Front -------------------------------*/
  return (
    <>
      <HomeLeftBar items={ NavigationService.getNavBarItems( token, dispatch ) } needConfirmation={ hasChange } />
      <div className='flex flex-col min-w-[350px] h-full w-full'>
        <HomeTopBar title={ t( 'Create client' ) } needConfirmationToNav={ hasChange }/>
        <Breadcrumb
          needConfirmationToNav={ hasChange }
          links={
            [
              { label: t( 'common:clients' ), route: ROUTES.CLIENTS, icon: ( <ClientsIcon className='h-[14px] select-none cursor-pointer'/> ) },
              { label: t( 'Create new client' ), route: ROUTES.NEW_CLIENT, icon: ( <PlusIcon className='h-[12px] select-none cursor-pointer'/> ) }
            ]
          }
        />
        {
          !isLoading ?
            <div className='flex flex-col items-center justify-center'>
              <form className='relative flex flex-col gap-4 h-full w-full mb-12 items-center justify-center p-4 px-[10%]'>

                <Input
                  required
                  maxLength={ MAX_LENGTH_ENTITY.UF_CLIENT_NAME }
                  label={ t( 'Client name' ) }
                  placeholder={ t( 'Client' ) + '...' }
                  icon={ <ClientsIcon className='w-7 h-7' /> }
                  value={ clientName }
                  onChange={ newClientName => {
                    setClientName( newClientName );
                  } }
                />

                <MultiSelect
                  label={ t( 'Administrators' ) }
                  placeholder={ t( 'No associated users' ) }
                  icon={ <UsersIcon className='w-7 h-7' /> }
                  clearable={ false }
                  selectedValues={ selectedAdmins }
                  getValues={ getUsers }
                  onChange={ options => {
                    setSelectedAdmins( options );
                  } }
                  extra={ selectedValue => {
                    return (
                      <SwitchAdminUser
                        objectId={ selectedValue.value }
                        isDisabled={ true }
                        defaultValue={ selectedValue.object.isAdmin }
                        onChange={ value => {
                          selectedValue.object.isAdmin = value;
                          const findedUser = selectedAdmins.find( item => { return selectedValue.object.id == item.value;  } );
                          if ( findedUser ) {
                            findedUser.object.isAdmin = value;
                          }
                          setSelectedAdmins( structuredClone( selectedAdmins ) );
                        } }
                      />
                    );
                  } }
                />

                <div className='flex h-fit w-1/3 min-w-[160px] max-w-[250px] justify-center mt-10'>
                  <Button
                    label={ t( 'Create' ) }
                    disable={ !canPost }
                    icon={ <SaveIcon className='h-[23px] w-[23px] stroke-white'/> }
                    color='#0bb173'
                    colorHover='#088D5C'
                    onClick={ onCreateClick }
                  />
                </div>
              </form>
            </div>
            :
            <Loading/>
        }
      </div>
    </>
  );

}